/**
 * Status, color, and display config for post-its.
 */
import { CheckCircle, AlertCircle, Clock } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type {
  PostItColor,
  PostItThematic,
  PostItStatus,
  RecurrenceMode,
  ScheduleType,
  WeekOfMonth,
  MonthNumber,
  DayOfWeek,
  QuarterNumber,
} from '@/types/post-it.types';
import type { BadgeVariant } from '@/components/common/LabelBadge';
import { getRoleLabel, getRoleLabelPlural } from '@/utils/role-label-utils';

/** Badge + icon config for a post-it status. */
export interface PostItStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor: string;
  dotColor: string;
}

/** Badge config for each post-it status. */
export const POST_IT_STATUS_CONFIG: Record<PostItStatus, PostItStatusConfig> = {
  done: {
    label: 'Terminé',
    icon: CheckCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-success))',
    dotColor: 'rgb(var(--status-success))',
  },
  depasse: {
    label: 'En retard',
    icon: AlertCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
    dotColor: 'rgb(var(--color-error))',
  },
  en_cours: {
    label: 'En cours',
    icon: Clock,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-1-nuance-1)',
    dotColor: 'var(--color-1-nuance-1)',
  },
};

/** Tailwind background class keyed by post-it color name. */
export const COLOR_MAP: Record<string, string> = {
  jaune: 'bg-postit-jaune',
  orange: 'bg-postit-orange',
  rose: 'bg-postit-rose',
  vert: 'bg-postit-vert',
  bleu: 'bg-postit-bleu',
  violet: 'bg-postit-violet',
  turquoise: 'bg-postit-turquoise',
  'blanc-casse': 'bg-postit-blanc-casse',
};

/** Tailwind icon text class keyed by post-it color name. */
export const ICON_COLOR_MAP: Record<string, string> = {
  jaune: 'text-postit-jaune-button',
  orange: 'text-postit-orange-button',
  rose: 'text-postit-rose-button',
  vert: 'text-postit-vert-button',
  bleu: 'text-postit-bleu-button',
  violet: 'text-postit-violet-button',
  turquoise: 'text-postit-turquoise-button',
  rouge: 'text-postit-rouge-button',
  'blanc-casse': 'text-postit-blanc-casse-button',
};

/** Tailwind body text class keyed by post-it color name. */
export const TEXT_COLOR_MAP: Record<string, string> = {
  jaune: 'text-postit-jaune-text',
  orange: 'text-postit-orange-text',
  rose: 'text-postit-rose-text',
  vert: 'text-postit-vert-text',
  bleu: 'text-postit-bleu-text',
  violet: 'text-postit-violet-text',
  turquoise: 'text-postit-turquoise-text',
  rouge: 'text-postit-rouge-text',
  'blanc-casse': 'text-postit-blanc-casse-text',
};

/** Tailwind border class keyed by post-it color name. */
export const BORDER_COLOR_MAP: Record<string, string> = {
  jaune: 'border-postit-jaune-placeholder/30',
  orange: 'border-postit-orange-placeholder/30',
  rose: 'border-postit-rose-placeholder/30',
  vert: 'border-postit-vert-placeholder/30',
  bleu: 'border-postit-bleu-placeholder/30',
  violet: 'border-postit-violet-placeholder/30',
  turquoise: 'border-postit-turquoise-placeholder/30',
  rouge: 'border-postit-rouge-placeholder/30',
  'blanc-casse': 'border-postit-blanc-casse-text',
};

/** Tailwind background class for an active schedule chip. */
export const SCHEDULE_CHIP_ACTIVE_BG_MAP: Record<string, string> = {
  jaune: 'bg-postit-jaune-button',
  orange: 'bg-postit-orange-button',
  rose: 'bg-postit-rose-button',
  vert: 'bg-postit-vert-button',
  bleu: 'bg-postit-bleu-button',
  violet: 'bg-postit-violet-button',
  turquoise: 'bg-postit-turquoise-button',
  rouge: 'bg-postit-rouge-button',
  'blanc-casse': 'bg-postit-blanc-casse-button',
};

/** Tailwind text class for an active schedule chip. */
export const SCHEDULE_CHIP_ACTIVE_TEXT_MAP: Record<string, string> = {
  jaune: 'text-postit-jaune',
  orange: 'text-postit-orange',
  rose: 'text-postit-rose',
  vert: 'text-postit-vert',
  bleu: 'text-postit-bleu',
  violet: 'text-postit-violet',
  turquoise: 'text-postit-turquoise',
  rouge: 'text-postit-rouge',
  'blanc-casse': 'text-postit-blanc-casse',
};

/** Tailwind border class for an active schedule chip. */
export const SCHEDULE_CHIP_ACTIVE_BORDER_MAP: Record<string, string> = {
  jaune: 'border-postit-jaune-button',
  orange: 'border-postit-orange-button',
  rose: 'border-postit-rose-button',
  vert: 'border-postit-vert-button',
  bleu: 'border-postit-bleu-button',
  violet: 'border-postit-violet-button',
  turquoise: 'border-postit-turquoise-button',
  rouge: 'border-postit-rouge-button',
  'blanc-casse': 'border-postit-blanc-casse-button',
};

/** Tailwind text class for an inactive schedule chip. */
export const SCHEDULE_CHIP_INACTIVE_TEXT_MAP: Record<string, string> = {
  jaune: 'text-postit-jaune-button',
  orange: 'text-postit-orange-button',
  rose: 'text-postit-rose-button',
  vert: 'text-postit-vert-button',
  bleu: 'text-postit-bleu-button',
  violet: 'text-postit-violet-button',
  turquoise: 'text-postit-turquoise-button',
  rouge: 'text-postit-rouge-button',
  'blanc-casse': 'text-postit-blanc-casse-button',
};

/** Tailwind border class for an inactive schedule chip. */
export const SCHEDULE_CHIP_INACTIVE_BORDER_MAP: Record<string, string> = {
  jaune: 'border-postit-jaune-button/30',
  orange: 'border-postit-orange-button/30',
  rose: 'border-postit-rose-button/30',
  vert: 'border-postit-vert-button/30',
  bleu: 'border-postit-bleu-button/30',
  violet: 'border-postit-violet-button/30',
  turquoise: 'border-postit-turquoise-button/30',
  rouge: 'border-postit-rouge-button/30',
  'blanc-casse': 'border-postit-blanc-casse-button/30',
};

/** Modal color tokens (bg, hover, text, border) keyed by {@link PostItColor}. */
export const COLOR_MAP_MODAL: Record<
  PostItColor,
  { bg: string; hoverBg: string; text: string; border: string }
> = {
  jaune: {
    bg: 'bg-postit-jaune',
    hoverBg: 'hover:bg-postit-jaune',
    text: 'text-amber-900',
    border: 'border-amber-700',
  },
  orange: {
    bg: 'bg-postit-orange',
    hoverBg: 'hover:bg-postit-orange',
    text: 'text-orange-900',
    border: 'border-orange-400',
  },
  rose: {
    bg: 'bg-postit-rose',
    hoverBg: 'hover:bg-postit-rose',
    text: 'text-pink-900',
    border: 'border-pink-400',
  },
  vert: {
    bg: 'bg-postit-vert',
    hoverBg: 'hover:bg-postit-vert',
    text: 'text-green-900',
    border: 'border-green-500',
  },
  violet: {
    bg: 'bg-postit-violet',
    hoverBg: 'hover:bg-postit-violet',
    text: 'text-purple-900',
    border: 'border-purple-400',
  },
  bleu: {
    bg: 'bg-postit-bleu',
    hoverBg: 'hover:bg-postit-bleu',
    text: 'text-blue-900',
    border: 'border-blue-400',
  },
  turquoise: {
    bg: 'bg-postit-turquoise',
    hoverBg: 'hover:bg-postit-turquoise',
    text: 'text-teal-900',
    border: 'border-teal-400',
  },
  'blanc-casse': {
    bg: 'bg-postit-blanc-casse',
    hoverBg: 'hover:bg-postit-blanc-casse',
    text: 'text-postit-blanc-casse-text',
    border: 'border-postit-blanc-casse-button',
  },
};

/** Selectable post-it colors in the picker (excludes blanc-casse). */
export const COLORS: PostItColor[] = [
  'jaune',
  'orange',
  'rose',
  'vert',
  'violet',
  'bleu',
  'turquoise',
];

/** Emoji keyed by thematic slug. */
export const THEMATIC_ICONS: Record<string, string> = {
  seances: '📚',
  matching: '🤝',
  tuteur: '👨‍🏫',
  eleves: '🎓',
  etablissements: '🏫',
  administration: '📋',
  formation: '🏋️',
  rapport_activite: '📊',
  autres: '📌',
};

/** Thematic options (value, French label, emoji) for pickers. */
export const THEMATICS: { value: PostItThematic; label: string; emoji: string }[] = [
  { value: 'seances', label: 'Séances', emoji: '📚' },
  { value: 'matching', label: 'Matching', emoji: '🤝' },
  {
    value: 'tuteur',
    get label() {
      return getRoleLabel('tutor');
    },
    emoji: '👨‍🏫',
  },
  {
    value: 'eleves',
    get label() {
      return getRoleLabelPlural('student');
    },
    emoji: '🎓',
  },
  { value: 'etablissements', label: 'Établissements', emoji: '🏫' },
  { value: 'administration', label: 'Administration', emoji: '📋' },
  { value: 'formation', label: 'Formation', emoji: '🏋️' },
  { value: 'rapport_activite', label: "Rapport d'activité", emoji: '📊' },
  { value: 'autres', label: 'Autres', emoji: '📌' },
];

/** Deadline period chips for the post-it filter. */
export type DeadlineFilter = 'all' | 'week' | 'month' | 'quarter' | 'year' | 'specific_date';

/** Deadline period options for the post-it filter. */
export const POST_IT_PERIOD_OPTIONS: { value: DeadlineFilter; label: string }[] = [
  { value: 'all', label: 'Tous' },
  { value: 'week', label: 'Cette semaine' },
  { value: 'month', label: 'Ce mois' },
  { value: 'quarter', label: 'Trimestriel' },
  { value: 'year', label: 'Cette année' },
  { value: 'specific_date', label: 'Date spécifique' },
];

/** School-year quarter chips (T1 Sep–Dec through T4 Jul–Aug). */
export const POST_IT_QUARTER_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: '1er trimestre' },
  { value: 2, label: '2ème trimestre' },
  { value: 3, label: '3ème trimestre' },
  { value: 4, label: '4ème trimestre' },
];

/** Short quarter chips (T1–T4) for schedule pickers. */
export const POST_IT_QUARTER_SHORT_OPTIONS: { value: QuarterNumber; label: string }[] = [
  { value: 1, label: 'T1' },
  { value: 2, label: 'T2' },
  { value: 3, label: 'T3' },
  { value: 4, label: 'T4' },
];

/** Assigned-board filter chips, including "all". */
export type AssignedFilter =
  | 'all'
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'annually'
  | 'specific_date';

/** Assigned-board filter options, including "all". */
export const POST_IT_ASSIGNED_FILTER_OPTIONS: {
  value: AssignedFilter;
  label: string;
}[] = [
  { value: 'all', label: 'Tous' },
  { value: 'weekly', label: 'Hebdomadaire' },
  { value: 'monthly', label: 'Mensuel' },
  { value: 'quarterly', label: 'Trimestriel' },
  { value: 'annually', label: 'Annuel' },
  { value: 'specific_date', label: 'Date spécifique' },
];

/** Schedule type chips for an assigned post-it. */
export const POST_IT_SCHEDULE_TYPE_OPTIONS: Array<{
  value: ScheduleType;
  label: string;
  icon: string;
}> = [
  { value: 'weekly', label: 'Hebdomadaire', icon: '📅' },
  { value: 'monthly', label: 'Mensuel', icon: '📆' },
  { value: 'quarterly', label: 'Trimestriel', icon: '🗓️' },
  { value: 'school_year', label: 'Année scolaire', icon: '🎓' },
  { value: 'civil_year', label: 'Année civile', icon: '🎉' },
  { value: 'day_of_week', label: 'Jour/semaine', icon: '📍' },
  { value: 'day_of_month', label: 'Jour/mois', icon: '📌' },
  { value: 'custom_dates', label: 'Dates libres', icon: '🗓' },
];

/** Default options applied when the user picks a schedule type. */
export const POST_IT_SCHEDULE_DEFAULT_OPTIONS: Record<ScheduleType, object> = {
  weekly: { targetMonths: 'all', weekOfMonth: 'all', yearScope: 'current_school_year' },
  monthly: { targetMonths: 'all', yearScope: 'current_school_year' },
  quarterly: { targetQuarters: [], yearScope: 'current_school_year' },
  school_year: { yearScope: 'current_school_year' },
  civil_year: { yearScope: 'current_civil_year' },
  day_of_week: { daysOfWeek: [], yearScope: 'current_school_year' },
  day_of_month: { dayNumber: 1, targetMonths: 'all', yearScope: 'current_school_year' },
  custom_dates: { dateFrom: '', yearScope: 'current_year' },
};

/** Recurrence modes available before a template exists. */
export const POST_IT_RECURRENCE_MODE_OPTIONS: Array<{
  value: RecurrenceMode;
  label: string;
  description: string;
}> = [
  { value: 'indefinitely', label: 'Indéfini', description: 'Toujours actif' },
  { value: 'n_times', label: 'Limité', description: 'N occurrences' },
];

/** Recurrence modes once the template exists and occurrences can be picked. */
export const POST_IT_RECURRENCE_MODE_WITH_PICK_OPTIONS: Array<{
  value: RecurrenceMode;
  label: string;
  description: string;
}> = [
  ...POST_IT_RECURRENCE_MODE_OPTIONS,
  { value: 'custom_pick', label: 'Sélection', description: 'Choisir manuellement' },
];

/** Abbreviated month chips. */
export const POST_IT_MONTH_OPTIONS: { value: MonthNumber; label: string }[] = [
  { value: 1, label: 'Jan' },
  { value: 2, label: 'Fév' },
  { value: 3, label: 'Mar' },
  { value: 4, label: 'Avr' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juin' },
  { value: 7, label: 'Juil' },
  { value: 8, label: 'Août' },
  { value: 9, label: 'Sep' },
  { value: 10, label: 'Oct' },
  { value: 11, label: 'Nov' },
  { value: 12, label: 'Déc' },
];

/** Week-of-month chips for a weekly schedule. */
export const POST_IT_WEEK_OF_MONTH_OPTIONS: Array<{ value: WeekOfMonth | 'all'; label: string }> = [
  { value: 'all', label: 'Toutes' },
  { value: 1, label: '1re' },
  { value: 2, label: '2e' },
  { value: 3, label: '3e' },
  { value: 4, label: '4e' },
  { value: 'last', label: 'Dernière' },
];

/** Weekday chips, Monday first. */
export const POST_IT_WEEKDAY_OPTIONS: Array<{ value: DayOfWeek; label: string }> = [
  { value: 1, label: 'Lun' },
  { value: 2, label: 'Mar' },
  { value: 3, label: 'Mer' },
  { value: 4, label: 'Jeu' },
  { value: 5, label: 'Ven' },
  { value: 6, label: 'Sam' },
  { value: 0, label: 'Dim' },
];

/** School-year scope chips for a schedule. */
export const POST_IT_SCHOOL_YEAR_SCOPE_OPTIONS = [
  { value: 'current_school_year' as const, label: 'Année scolaire en cours' },
  { value: 'all_school_years' as const, label: 'Toutes les années scolaires' },
];

/** Civil-year scope chips for a schedule. */
export const POST_IT_CIVIL_YEAR_SCOPE_OPTIONS = [
  { value: 'current_civil_year' as const, label: 'Année civile en cours' },
  { value: 'all_civil_years' as const, label: 'Toutes les années civiles' },
];

/** Custom-date scope chips for a schedule. */
export const POST_IT_CUSTOM_DATE_SCOPE_OPTIONS = [
  { value: 'current_year', label: 'Année en cours' },
  { value: 'selected_years', label: 'Années spécifiques' },
  { value: 'indefinitely', label: 'Indéfiniment' },
];
